// Pure game logic for Fairway Stakes.

export const HOLE_COUNT = 18;
export const STARTING_BET = 3;

// Bet on a given hole. Starts at `startingBet` on hole 1 (default $3) and
// increases by $1 only for each PRIOR hole that had an outright winner.
// Tied holes freeze the bet, so the next hole is worth the same amount.
export function holeBet(holeIndex, holes, startingBet = STARTING_BET) {
    let bet = startingBet;
    for (let i = 0; i < holeIndex; i++) {
        if (holes?.[i]?.result?.status === "won") bet += 1;
    }
    return bet;
}

// Maximum possible pot: every hole won outright (no ties).
export function maxTotalPot(startingBet = STARTING_BET) {
    let s = 0;
    let bet = startingBet;
    for (let i = 0; i < HOLE_COUNT; i++) {
        s += bet;
        bet += 1;
    }
    return s;
}

// Allocate strokes for a player across 18 holes using USGA-style rule:
// base = floor(handicap / 18) strokes on every hole
// extra = handicap % 18 additional strokes on holes with rating 1..extra (hardest holes)
export function allocateStrokes(handicap, holeHandicapRatings) {
    const h = Math.max(0, Math.floor(handicap || 0));
    const base = Math.floor(h / 18);
    const extra = h % 18;
    const out = new Array(HOLE_COUNT).fill(0);
    for (let i = 0; i < HOLE_COUNT; i++) {
        const rating = holeHandicapRatings[i];
        out[i] = base + (rating <= extra ? 1 : 0);
    }
    return out;
}

// Compute the result of a single hole given all players' gross scores.
// grossScores: array indexed by player, values are numbers or null.
// Returns:
//   { status: "incomplete" | "tied" | "won",
//     winnerIdx: number|null,
//     nets: [numbers/nulls],
//     strokes: [numbers],
//     birdie: boolean,
//     eagle: boolean,
//     bet, payout }
// If tied on lowest net, no one wins and no money is awarded — the hole is
// void AND the next hole's bet stays at this hole's amount (bet does not
// increment). Natural (gross) score-based bonuses on the winning hole:
//   • Birdie (gross = par-1)         → 2× payout
//   • Eagle or better (gross ≤ par-2) → 4× payout
export function computeHole({
    holeIndex,
    par,
    holeHandicapRatings,
    players,
    grossScores,
    holes,
    startingBet = STARTING_BET,
    eagleEnabled = true,
}) {
    const minHcp = Math.min(...players.map((p) => Number(p.handicap) || 0));
    const strokes = players.map((p) => {
        const playingHcp = Math.max(0, (Number(p.handicap) || 0) - minHcp);
        return allocateStrokes(playingHcp, holeHandicapRatings);
    });
    const strokesThisHole = strokes.map((s) => s[holeIndex]);
    const nets = grossScores.map((g, i) =>
        g == null || g === "" ? null : Number(g) - strokesThisHole[i],
    );

    const bet = holeBet(holeIndex, holes, startingBet);
    const anyMissing = nets.some((n) => n == null);
    if (anyMissing) {
        return {
            status: "incomplete",
            winnerIdx: null,
            nets,
            strokesThisHole,
            birdie: false,
            eagle: false,
            bet,
            payout: 0,
        };
    }

    const minNet = Math.min(...nets);
    const winners = nets
        .map((n, i) => (n === minNet ? i : -1))
        .filter((i) => i >= 0);

    if (winners.length > 1) {
        return {
            status: "tied",
            winnerIdx: null,
            nets,
            strokesThisHole,
            birdie: false,
            eagle: false,
            bet,
            payout: 0,
        };
    }

    const winnerIdx = winners[0];
    const gross = Number(grossScores[winnerIdx]);
    // Natural (gross) bonuses — not net via handicap.
    const isEagle = eagleEnabled && gross <= par - 2;
    const isBirdie = !isEagle && gross === par - 1;
    const payout = isEagle ? bet * 4 : isBirdie ? bet * 2 : bet;

    return {
        status: "won",
        winnerIdx,
        nets,
        strokesThisHole,
        birdie: isBirdie,
        eagle: isEagle,
        bet,
        payout,
    };
}

// Live payout for a hole based on the current holes state. Used everywhere
// payouts are DISPLAYED so that editing an earlier hole keeps totals correct.
export function holePayout(holeIndex, holes, startingBet = STARTING_BET) {
    const r = holes?.[holeIndex]?.result;
    if (!r || r.status !== "won") return 0;
    const bet = holeBet(holeIndex, holes, startingBet);
    if (r.eagle) return bet * 4;
    if (r.birdie) return bet * 2;
    return bet;
}

// Per-player running winnings ($). Winner receives the hole's payout;
// each of the (n-1) others pays equal share. Payouts recompute on the fly
// so mid-round edits stay consistent.
export function computeStandings(state) {
    const n = state.players.length;
    const startingBet = state.startingBet ?? STARTING_BET;
    const totals = new Array(n).fill(0);
    state.holes.forEach((h, i) => {
        const r = h?.result;
        if (!r || r.status !== "won" || r.winnerIdx == null) return;
        const payout = holePayout(i, state.holes, startingBet);
        const share = payout / (n - 1);
        for (let j = 0; j < n; j++) {
            if (j === r.winnerIdx) totals[j] += payout;
            else totals[j] -= share;
        }
    });
    return totals;
}

export function formatMoney(v) {
    const sign = v < 0 ? "-" : "";
    const abs = Math.abs(v);
    if (Math.abs(abs - Math.round(abs)) < 0.005) {
        return `${sign}$${Math.round(abs)}`;
    }
    return `${sign}$${abs.toFixed(2)}`;
}
